import { z } from "zod";
import { Printable } from "./feedback.ts";
import { GroupId, MAILING_LIST_MEMBERS_MAX } from "./groups.ts";
import { MediaKind } from "./media.ts";
import { ExternalCallUrl, ExternalCallUrlView } from "./schedule.ts";
import { UserId } from "./user.ts";

/**
 * Материал, выданный ученикам, и квитанция о его открытии.
 *
 * Проект целиком: `docs/material-receipt-design.md`. Здесь часть А: материал
 * выдаётся в продукте и открывается в продукте, квитанция снимается по сессии.
 * Публичной страницы `/m/<id>`, обезличенного счётчика и копируемой ссылки в
 * этих схемах нет ни одного поля, и это граница части Б, которая не включается
 * раньше юридического контура (раздел 3 проекта).
 *
 * ⚠️ **Это НЕ домашка, и ни одно поле отсюда в неё не уезжает** (раздел 4.3
 * проекта). Ни срока, ни проверки, ни отметки «выполнено», ни попадания в
 * счётчики домашки: у материала нет ответа, который можно проверить.
 * `HomeworkSource` не трогается, это дискриминированный юнион источников
 * УПРАЖНЕНИЙ.
 *
 * ⚠️ **Проверка адреса берётся у `ExternalCallUrl`, а не пишется заново**
 * (раздел 8 проекта). Класс адресов один и тот же: чужая ссылка, введённая
 * одним человеком и показываемая несовершеннолетнему. Два правила про один
 * класс расходятся молча, а разошедшись, дают адрес, который проходит одну
 * границу и не проходит другую, то есть 500 на вставке вместо внятного отказа.
 * Домен рядом с кнопкой показывается `externalCallHost` из того же файла.
 *
 * ⚠️ **Названия группы и числа получателей нет ни в одном поле, которое читает
 * ученик** (раздел 7 проекта): материал не становится четвёртым каналом
 * раскрытия состава скрытой группы. Список получателей с именами уезжает только
 * в ответе преподавателю на его же выдачу и в его же список.
 */

export const MaterialId = z.string().uuid();
export type MaterialId = z.infer<typeof MaterialId>;

/* ──────────────────────────── потолки ──────────────────────────── */

/**
 * Потолок названия. То же число, что в `CHECK` базы
 * (`length(title) BETWEEN 1 AND 200`), и разъехаться им нельзя: база ответила бы
 * `23514`, то есть пятисоткой, вместо внятного отказа на вводе.
 */
export const MATERIAL_TITLE_MAX = 200;

/**
 * Потолок поимённой адресации одной выдачи.
 *
 * Число то же, что у рассылочного списка и у объявления, и это намеренно:
 * адресация группой и поимённая кладут ОДНИ И ТЕ ЖЕ строки получателей, поэтому
 * разные потолки означали бы, что один и тот же набор людей адресуем одним
 * способом и неадресуем другим.
 */
export const MATERIAL_RECIPIENTS_MAX = MAILING_LIST_MEMBERS_MAX;

/**
 * Страница списка материалов у преподавателя (раздел 5 проекта).
 *
 * Keyset-курсор, `offset` в проекте не используется ни разу.
 */
export const MATERIAL_PAGE_LIMIT = 50;

/**
 * Срок хранения материала и его квитанций (раздел 10 проекта).
 *
 * ⚠️ **Срок обязан быть, вопреки первому желанию оставить факт навсегда.**
 * «Факт живёт вечно» противоречит ст. 5 ч. 7 и ст. 21 152-ФЗ (уничтожать по
 * достижении цели) и пункту 9 таблицы раздела 7 стратегии. Категория данных тут
 * не ограничена по построению: `title` и `url` задаёт преподаватель свободным
 * текстом, поэтому «какой внешний ресурс и когда открыл названный ребёнок»
 * может оказаться чем угодно. Технически это не ограничить, значит
 * ограничивается политикой и сроком.
 *
 * ⚠️ Число живёт здесь, а не в уборщике, по общему правилу: срок, названный в
 * двух местах, однажды разъедется. Это же число назовёт человеку экран и строка
 * политики обработки, поэтому оно константа общего пакета, а не переменная
 * окружения.
 */
export const MATERIAL_RETENTION_DAYS = 365;

/**
 * Через сколько суток после нажатия «удалить» материал уходит ФИЗИЧЕСКИ, вместе
 * с квитанциями.
 *
 * ⚠️ **Второй срок обязателен, и без него первый не выполняет своего
 * обещания.** Мягкое удаление ставит `deleted_at` и убирает карточку с глаз, а
 * строка при этом лежала бы до года: свободный текст преподавателя и запись
 * «этот ребёнок открыл это тогда-то». Весь юридический довод раздела 10 проекта
 * держится на «уничтожать по достижении цели» (ст. 5 ч. 7 и ст. 21 152-ФЗ), а
 * нажатая кнопка это и есть заявление о её достижении.
 *
 * ⚠️ **Это НЕ срок корзины, хотя число то же.** У корзины медиатеки, уроков,
 * заготовок и домашек есть «восстановить», и тридцать суток там это окно для
 * человека; у материала восстановления нет вовсе, и окно здесь нужно на другое:
 * пережить ошибочное нажатие настолько, чтобы её успели заметить и пожаловаться.
 * Поэтому число своё, а не `RETENTION_DAYS` уборщика: общая переменная означала
 * бы, что срок уничтожения ПДн двигается настройкой корзины.
 */
export const MATERIAL_TRASH_DAYS = 30;

/* ─────────────────────────── поля выдачи ───────────────────────── */

/**
 * Вид материала. Дискриминант один на объект, и третьего значения у него сейчас
 * нет намеренно.
 *
 * ⚠️ Цена третьего вида названа заранее (раздел 5 проекта): `CHECK` в базе
 * придётся снимать и заводить заново на непустой таблице, потому что `DROP` в
 * миграциях проекта запрещён.
 */
export const MaterialKind = z.enum(["link", "file"]);
export type MaterialKind = z.infer<typeof MaterialKind>;

/**
 * Название материала, которое читает ученик.
 *
 * ⚠️ **`Printable` здесь обязателен, а не аккуратен.** Это свободный текст,
 * который вставляют из PDF, а PostgreSQL не хранит нулевой байт ни в `text`, ни
 * в `jsonb`: вставка падает ошибкой драйвера, то есть пятисоткой (разбор у
 * самого `Printable` в `./feedback.ts`).
 */
export const MaterialTitle = Printable.trim().min(1).max(MATERIAL_TITLE_MAX);
export type MaterialTitle = z.infer<typeof MaterialTitle>;

/* ──────────────────────────── создание ─────────────────────────── */

/**
 * Адресация выдачи: ровно одна из двух, группой либо поимённым списком.
 *
 * ⚠️ **Взаимоисключение держит `refine`, а не комментарий**, ровно как у
 * объявления. Пока оба поля были просто необязательными, материал можно было
 * адресовать поимённо И пометить группой, а `group_id` при этом читается как
 * «как собрали список»: получатель, которого в группе нет, оказался бы помечен
 * чужой группой.
 *
 * ⚠️ **Пустая адресация это НЕ отказ, и зеркала `announcement_empty` здесь
 * нет** (раздел 13 проекта). Материал без получателей законен: это и есть
 * «скопировал ссылку», ради чего заведена часть Б. Объявление без получателей
 * не означает ничего, а материал означает выданный адрес.
 */
const addressing = {
  groupId: GroupId.optional(),
  /** Поимённая адресация. Выдача одному это список из одного. */
  studentUserIds: z.array(UserId).min(1).max(MATERIAL_RECIPIENTS_MAX).optional(),
};

/**
 * Ключ идемпотентности создания, придуманный клиентом.
 *
 * ⚠️ Обязателен, прецедент у домашки (`homework.request_id`), а не у
 * объявления, где идемпотентности нет. Одно нажатие разворачивается в N строк
 * получателей, и повтор после обрыва дал бы ученику вторую квитанцию на тот же
 * материал. Повтор с тем же ключом возвращает ТОТ ЖЕ материал.
 */
const requestId = z.string().uuid();

/**
 * Создание материала. Дискриминированный юнион по `kind`, а не объект с двумя
 * необязательными полями: у вида `link` обязан быть адрес и не может быть
 * файла, у вида `file` наоборот, и `CHECK` в базе (`material_target`) говорит
 * ровно это. Объект с двумя `optional` разрешал бы оба разом и оба пустыми, то
 * есть отдавал бы отсечку базе, а она отвечает пятисоткой.
 */
export const CreateMaterialRequest = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("link"),
      requestId,
      title: MaterialTitle,
      /**
       * ⚠️ Та же схема, что у ссылки на созвон, и ровно поэтому: только
       * `https`, ни одного пробельного символа, обязан разбираться `new URL`.
       * Первая редакция проекта разрешала `http` и не требовала разбора, то есть
       * была СЛАБЕЕ уже действующего правила для того же класса адресов.
       */
      url: ExternalCallUrl,
      ...addressing,
    }),
    z.object({
      kind: z.literal("file"),
      requestId,
      title: MaterialTitle,
      /**
       * Файл медиатеки. ⚠️ Владельца проверяет сервер (`requireOwnedFile`), и
       * составным ключом это не закрыть: у `media_file` нет
       * `UNIQUE (id, owner_user_id)`, в отличие от `announcement` и
       * `student_group` (раздел 7 проекта).
       */
      fileId: z.string().uuid(),
      ...addressing,
    }),
  ])
  .refine((v) => v.groupId === undefined || v.studentUserIds === undefined, {
    message: "адресация ровно одна: либо группа, либо поимённый список",
    path: ["groupId"],
  });
export type CreateMaterialRequest = z.infer<typeof CreateMaterialRequest>;

/**
 * Кого выдача не застала и почему.
 *
 * ⚠️ **Молчаливой деградации у выдачи нет** (раздел 5.5 CLAUDE.md, раздел 7
 * проекта). Названного поимённо отвязанного ученика ручка отвергает целиком
 * (`409 student_not_linked`): преподаватель выбрал именно его. Отвязанный
 * участник группы выдачу не останавливает, но и не исчезает молча: группа это
 * «как собрали список», а «выдано восьмерым» вместо десяти без единого слова о
 * двоих это ровно то, что запрещено.
 *
 * ⚠️ **Своя схема, а не `AnnouncementSkipped`, при одинаковой форме.** Это не
 * дублирование по недосмотру: рассылка и материал это разные объекты с разными
 * сроками и разными причинами пропуска, а общий тип означал бы, что следующая
 * причина, заведённая у одного, молча появляется в контракте другого.
 */
export const MaterialSkipped = z.object({
  studentUserId: UserId,
  /**
   * Имя ученика из списка ПРЕПОДАВАТЕЛЯ (`app_user.display_name`).
   *
   * ⚠️ Строка едет в ответе на СОБСТВЕННУЮ выдачу преподавателя и ребёнку не
   * показывается ни одним путём. Без имени на экране остаётся «ещё 2», то есть
   * человек узнаёт, что кому-то не ушло, и не узнаёт кому.
   */
  studentName: z.string(),
  reason: z.enum(["unlinked"]),
});
export type MaterialSkipped = z.infer<typeof MaterialSkipped>;

/**
 * Ответ на создание материала.
 *
 * ⚠️ `recipients` может быть НУЛЁМ, в отличие от объявления: материал без
 * получателей законен (см. `CreateMaterialRequest`).
 */
export const CreateMaterialResponse = z.object({
  id: MaterialId,
  /**
   * ⚠️ **«Эта выдача уже была», и экран обязан на это смотреть.** Повтор по тому
   * же `requestId` возвращает ТОТ ЖЕ материал (раздел 5 проекта), а `skipped` у
   * него ПУСТОЙ и пустым остаётся намеренно: список пропущенных это отчёт о том,
   * что произошло при ВЫДАЧЕ, а пара могла отвязаться после, и пересчёт
   * сегодняшними данными назвал бы пропущенным того, кто материал получил.
   *
   * Отсюда требование к вебу: при `repeated === true` печатать «эта выдача уже
   * была», а не числа, будто они свежие. Иначе преподаватель, у которого связь
   * оборвалась на групповой выдаче, увидит «выдано восьмерым» и ни слова про
   * двоих отвязанных, то есть ровно ту молчаливую деградацию, которую запрещает
   * раздел 5.5 CLAUDE.md. Признаком, а не кодом ответа: `200` против `201`
   * различает то же самое, но до экрана доезжает не всегда (обёртки клиента
   * схлопывают 2xx).
   */
  repeated: z.boolean(),
  /** Сколько человек получили. */
  recipients: z.number().int().min(0),
  /** Кого пропустили. Пустой массив это норма, а не отсутствие поля. */
  skipped: z.array(MaterialSkipped),
});
export type CreateMaterialResponse = z.infer<typeof CreateMaterialResponse>;

/* ───────────────────────────── выдача ──────────────────────────── */

/**
 * Веха воспроизведения файла: корзина по 25%, а не секунды.
 *
 * ⚠️ Точнее корзины продукт не печатает (раздел 6.2 проекта): «досмотрел до
 * 4:12» у дошедшего до 49% это недоговорка на четыре минуты уверенным тоном.
 * Те же четыре значения, что в `CHECK` базы.
 */
export const MaterialMilestone = z.union([
  z.literal(25),
  z.literal(50),
  z.literal(75),
  z.literal(100),
]);
export type MaterialMilestone = z.infer<typeof MaterialMilestone>;

/**
 * Тело вехи `POST /v1/materials/:materialId/progress` (раздел 13 проекта).
 *
 * Только `kind='file'`: у ссылки воспроизведение идёт не у нас. Ученику,
 * который шлёт веху ссылке, отвечают тем же `404 material_not_found`, что
 * постороннему: отдельный код рассказал бы, что материал есть и какого он вида.
 */
export const MaterialProgressRequest = z.object({ milestone: MaterialMilestone });
export type MaterialProgressRequest = z.infer<typeof MaterialProgressRequest>;

/**
 * Квитанция одного получателя.
 *
 * ⚠️ **Имена списком, а не два числа** (решение владельца 19.09.2026, развилка
 * 17.1 проекта). Это осознанное расхождение с формой рассылки («прочитали 3 из
 * 8»): под сообщением в ленте поимённый список повторялся бы у каждой строки, а
 * у материала квитанция это и есть содержимое карточки, и вопрос «кому
 * напомнить лично» здесь основной.
 */
export const MaterialReceipt = z.object({
  studentUserId: UserId,
  studentName: z.string(),
  /**
   * Когда открыл впервые. `null` это «отметки нет», а НЕ «не открыл»: слова про
   * пустую квитанцию выбирает экран, и он обязан различать четыре состояния
   * (раздел 6 проекта), а не два.
   */
  firstOpenedAt: z.number().int().nullable(),
  /**
   * Веха просмотра файла, корзина по 25%. `null` у ссылки всегда.
   *
   * ⚠️ Слова «досмотрел» здесь нет и быть не может: веха приезжает от клиента, а
   * перемотка в конец ставит 100% за три секунды (раздел 6.2 проекта).
   */
  milestone: MaterialMilestone.nullable(),
});
export type MaterialReceipt = z.infer<typeof MaterialReceipt>;

/**
 * Строка списка материалов.
 *
 * ⚠️ **Агрегата в адрес преподавателя здесь нет ни одного поля** (раздел 14
 * проекта, решение владельца 23.08.2026). «Выдано 12, открыто 3» это счёт его
 * работы; считается ученик, а не тот, кто смотрит на экран.
 */
export const MaterialListItem = z.object({
  id: MaterialId,
  kind: MaterialKind,
  title: z.string(),
  /**
   * Адрес у `kind='link'`.
   *
   * ⚠️ Схема на выходе нарочно слабее, чем на входе, и это тот же довод, что у
   * `ExternalCallUrlView`: значение в базе старше сегодняшних проверок и
   * переживёт их изменение, а строгая схема на ответе уронила бы весь список
   * из-за одной строки. Решение «показывать или нет» принимает `externalCallHost`.
   */
  url: ExternalCallUrlView.nullable(),
  /**
   * Файл у `kind='file'`.
   *
   * ⚠️ **`null` при `kind='file'` это ЗАКОННОЕ состояние и означает «файл
   * стёрт»** (раздел 5 проекта): экран показывает надгробие, а не пятисотку.
   */
  fileId: z.string().uuid().nullable(),
  /**
   * Вид файла у `kind='file'`: видео, звук, картинка или документ. Ученику он
   * нужен, чтобы выбрать просмотрщик, а прочитать `media_file` сам он не может,
   * файл чужой. `null` у ссылки и у стёртого файла.
   *
   * `default(null)`, а не обязательное поле, ровно как `fileKind` у медиатеки:
   * вкладка, открытая до выкатки, иначе уронила бы разбор всего списка.
   */
  fileKind: MediaKind.nullable().default(null),
  /**
   * Имя преподавателя, выдавшего материал (`app_user.display_name`), как
   * `counterpartName` у домашки. Ученику нужно затем, что преподавателей у него
   * бывает несколько; преподаватель видит здесь себя.
   */
  teacherName: z.string().default(""),
  createdAt: z.number().int(),
  /** Отзыв гасит адрес и не трогает квитанции. `null` это «работает». */
  revokedAt: z.number().int().nullable(),
  /**
   * Кому выдан и кто открыл. Пустой список это «скопировал ссылку».
   *
   * ⚠️ **Состав списка зависит от того, КТО спрашивает, и решается это в
   * ОДНОМ месте сборки на сервере** (`receiptsOf` в
   * `apps/api/src/repo/material-list.ts`), а не схемой.
   * Преподаватель видит всех получателей поимённо (развилка 17.1 проекта),
   * ученик видит РОВНО СВОЮ строку. Иначе материал стал бы четвёртым каналом
   * раскрытия состава скрытой группы (раздел 7 проекта), а три существующих
   * канала сторожатся отдельно каждый. Тот же приём, что у `marksOf`.
   */
  receipts: z.array(MaterialReceipt),
});
export type MaterialListItem = z.infer<typeof MaterialListItem>;

export const MaterialListResponse = z.object({
  items: z.array(MaterialListItem),
  /** `null` это «дальше ничего нет». Курсор клиент возвращает как есть. */
  nextCursor: z.string().nullable(),
});
export type MaterialListResponse = z.infer<typeof MaterialListResponse>;
